import { useCallback, useEffect, useState } from "react";
import { api, unwrapArray, unwrapObject } from "@/lib/api";
import { useToast } from "@/context/ToastContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ClipboardCheck, Loader2, PackagePlus, Plus, Trash2 } from "lucide-react";
import { qty, type OutputData, type WorkOrderRecord } from "@/types/production";
import { GRADES, STOCK_STATUS_LABELS, type StockUnitRecord, type WarehouseRecord } from "@/types/inventory";
import { QcDialog } from "@/components/inventory/QcDialog";

const field = "w-full h-10 rounded-lg border border-input bg-background px-3 text-sm focus:outline-hidden focus:ring-2 focus:ring-ring";
const cell = "h-8 w-full rounded-md border border-input bg-background px-2 text-xs focus:outline-hidden focus:ring-2 focus:ring-ring";

interface Draft { unit_no: string; quantity: string; grade: string; location: string }
const blank = (): Draft => ({ unit_no: "", quantity: "", grade: "FRESH", location: "" });

/** Finished rolls this work order has put into stock, and a form to add more */
export function OutputSection({ w, canUpdate, reloadKey, onChanged }: { w: WorkOrderRecord; canUpdate: boolean; reloadKey: number; onChanged: () => void }) {
  const [data, setData] = useState<OutputData | null>(null);
  const { error } = useToast();
  const [open, setOpen] = useState(false);
  const [inspect, setInspect] = useState<StockUnitRecord | null>(null);
  const load = useCallback(async () => {
    try {
      setData(unwrapObject<OutputData>(await api.get(`/api/ProductionStock/Outputs/${w.id}`)));
    } catch {
      setData(null);
    }
  }, [w.id]);
  useEffect(() => {
    void load();
  }, [load, reloadKey]);

  const stages = w.stages || [];
  const openStages = stages.filter((s) => s.stage_status === "PENDING" || s.stage_status === "IN_PROGRESS");
  const canReceive = canUpdate && (w.work_order_status === "COMPLETED" || (w.work_order_status === "IN_PROGRESS" && openStages.length === 1 && openStages[0]!.stage_status === "IN_PROGRESS"));
  if (!data || (!data.rows.length && !canReceive)) return null;

  return (
    <div className="bg-card border rounded-2xl overflow-hidden">
      <div className="px-4 py-3 border-b flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h3 className="text-sm font-bold">Finished goods in stock</h3>
          <p className="text-[11px] text-muted-foreground">{qty(data.total_qty, w.unit)} put into stock{data.produced_qty != null ? ` of ${qty(data.produced_qty, w.unit)} produced` : ""}</p>
        </div>
        {canReceive && (
          <Button size="sm" variant="outline" className="h-8 text-xs gap-1" onClick={() => setOpen(true)}>
            <PackagePlus className="size-3.5" /> Put into stock
          </Button>
        )}
      </div>
      {data.rows.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[11px] uppercase tracking-wider text-muted-foreground border-b">
                <th className="px-4 py-2 font-semibold">Roll</th>
                <th className="px-2 py-2 font-semibold">Lot</th>
                <th className="px-2 py-2 font-semibold">Grade</th>
                <th className="px-2 py-2 font-semibold text-right">Quantity</th>
                <th className="px-2 py-2 font-semibold">Now</th>
                {canUpdate && <th className="px-4 py-2" />}
              </tr>
            </thead>
            <tbody className="divide-y">
              {data.rows.map((r) => (
                <tr key={r.id}>
                  <td className="px-4 py-2 font-medium">{r.unit_no}</td>
                  <td className="px-2 py-2 text-xs text-muted-foreground">{r.lot_number}</td>
                  <td className="px-2 py-2 text-xs">{GRADES[r.grade] || r.grade}</td>
                  <td className="px-2 py-2 text-right">{qty(r.quantity, r.unit)}</td>
                  <td className="px-2 py-2 text-xs">{r.unit_status ? STOCK_STATUS_LABELS[r.unit_status] || r.unit_status : "-"}{r.reserved && r.unit_status === "RESERVED" ? " for the order" : ""}</td>
                  {canUpdate && (
                    <td className="px-4 py-2 text-right">
                      {(r.unit_status === "IN_STOCK" || r.unit_status === "RESERVED") && (
                        <Button size="sm" variant="ghost" className="h-7 text-xs gap-1" onClick={async () => {
                          try { setInspect(unwrapObject<StockUnitRecord>(await api.get(`/api/Inventory/UnitById/${r.unit_id}`))); } catch (e: any) { error("Could not open", e.message || "Please try again"); }
                        }}>
                          <ClipboardCheck className="size-3.5" /> Inspect
                        </Button>
                      )}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <QcDialog unit={inspect} workOrderId={w.id} onClose={() => setInspect(null)} onSaved={() => { void load(); onChanged(); }} />
      <OutputDialog w={w} open={open} onClose={() => setOpen(false)} onSaved={() => { void load(); onChanged(); }} />
    </div>
  );
}

function OutputDialog({ w, open, onClose, onSaved }: { w: WorkOrderRecord; open: boolean; onClose: () => void; onSaved: () => void }) {
  const { success, error } = useToast();
  const [warehouses, setWarehouses] = useState<WarehouseRecord[]>([]);
  const [warehouseId, setWarehouseId] = useState("");
  const [lot, setLot] = useState("");
  const [reserve, setReserve] = useState(true);
  const [rolls, setRolls] = useState<Draft[]>([blank()]);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (!open) return;
    setRolls([blank()]);
    setLot("");
    setReserve(true);
    api.get("/api/Warehouse/List").then((r) => {
      const list = unwrapArray<WarehouseRecord>(r);
      setWarehouses(list);
      setWarehouseId(list.find((x) => x.is_default)?.id || list[0]?.id || "");
    }).catch(() => setWarehouses([]));
  }, [open]);
  const set = (i: number, patch: Partial<Draft>) => setRolls(rolls.map((r, j) => (j === i ? { ...r, ...patch } : r)));
  const total = rolls.reduce((a, r) => a + (Number(r.quantity) || 0), 0);
  const submit = async () => {
    const filled = rolls.filter((r) => r.unit_no.trim() || r.quantity.trim());
    if (!filled.length) return error("Validation Error", "Add at least one roll");
    if (filled.some((r) => !r.unit_no.trim() || !(Number(r.quantity) > 0))) return error("Validation Error", "Every roll needs a number and a quantity above zero");
    setSaving(true);
    try {
      const res = unwrapObject<{ rolls: number; reserved: number }>(await api.post(`/api/ProductionStock/OutputReceive/${w.id}`, {
        warehouse_id: warehouseId,
        lot_number: lot.trim() || undefined,
        reserve_for_order: reserve,
        rolls: filled.map((r) => ({ unit_no: r.unit_no.trim(), quantity: Number(r.quantity), grade: r.grade, location: r.location.trim() || undefined })),
      }));
      success("Put into stock", `${res.rolls} roll${res.rolls === 1 ? "" : "s"}${res.reserved ? `, ${res.reserved} held for the order` : ""}`);
      onSaved();
      onClose();
    } catch (err: any) {
      error("Could not save", err.message || "Please try again");
    } finally {
      setSaving(false);
    }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Put {w.product_name} into stock</DialogTitle>
          <DialogDescription>Finished rolls of {w.wo_number}, in {w.unit}.{w.order ? " Good rolls are held for the order until it is covered." : ""}</DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-1 text-sm">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Warehouse</Label>
              <select className={field} value={warehouseId} onChange={(e) => setWarehouseId(e.target.value)}>
                {warehouses.map((x) => <option key={x.id} value={x.id}>{x.name}</option>)}
              </select>
            </div>
            <div className="space-y-1.5"><Label>Lot (default {w.wo_number})</Label><Input value={lot} onChange={(e) => setLot(e.target.value)} className="h-10" /></div>
          </div>
          {w.order && (
            <label className="flex items-center gap-2"><input type="checkbox" className="size-4" checked={reserve} onChange={(e) => setReserve(e.target.checked)} /> Hold the rolls for {w.order.order_number}</label>
          )}
          <div className="space-y-1.5">
            {rolls.map((r, i) => (
              <div key={i} className="grid grid-cols-12 gap-1.5 items-center">
                <input className={`${cell} col-span-3`} placeholder="Roll no." value={r.unit_no} onChange={(e) => set(i, { unit_no: e.target.value })} />
                <input className={`${cell} col-span-3`} placeholder={`Qty (${w.unit})`} inputMode="decimal" value={r.quantity} onChange={(e) => set(i, { quantity: e.target.value })} />
                <select className={`${cell} col-span-2`} value={r.grade} onChange={(e) => set(i, { grade: e.target.value })}>
                  {Object.entries(GRADES).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                </select>
                <input className={`${cell} col-span-3`} placeholder="Rack" value={r.location} onChange={(e) => set(i, { location: e.target.value })} />
                <button type="button" className="col-span-1 text-muted-foreground hover:text-red-600 disabled:opacity-30" disabled={rolls.length === 1} onClick={() => setRolls(rolls.filter((_, j) => j !== i))}><Trash2 className="size-3.5 mx-auto" /></button>
              </div>
            ))}
            <div className="flex items-center justify-between pt-1">
              <Button type="button" variant="ghost" size="sm" className="h-7 text-xs gap-1" onClick={() => setRolls([...rolls, blank()])}><Plus className="size-3" /> Add roll</Button>
              <span className="text-xs text-muted-foreground">Total {qty(Math.round(total * 1000) / 1000, w.unit)}</span>
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={saving}>{saving && <Loader2 className="size-4 animate-spin mr-1.5" />}Put into stock</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
